import {useId, useRef, useState, type ReactNode} from "react";
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger}
    from "@/components/ui/dialog.tsx";
import {Button} from "@/components/ui/button.tsx";
import {errorDescription} from "@/api/ErrorNotification.ts";
import {X} from "lucide-react";

export default function CombatantEditDialog({title, description, trigger, children, onOpen, onSave, validate, wide = false}: {
    title: string; description: string; trigger: ReactNode; children: ReactNode;
    onOpen: () => void; onSave: () => Promise<void>; validate?: () => string | undefined; wide?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [pending, setPending] = useState(false);
    const saving = useRef(false);
    const [error, setError] = useState<string>();
    const errorId = useId();
    return <Dialog open={open} onOpenChange={next => {
        if (saving.current) return;
        if (next) {onOpen(); setError(undefined);}
        setOpen(next);
    }}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent showCloseButton={false} className={wide ? "flex max-h-[calc(100dvh-2rem)] flex-col sm:max-w-150" : "flex max-h-[calc(100dvh-2rem)] flex-col sm:max-w-100"}>
            <Button type="button" variant="ghost" size="icon-sm" className="absolute right-3 top-3"
                aria-label="Dialog schließen" disabled={pending} onClick={() => setOpen(false)}><X aria-hidden="true"/></Button>
            <DialogHeader className="shrink-0 pr-8">
                <DialogTitle>{title}</DialogTitle>
                <DialogDescription>{description}</DialogDescription>
            </DialogHeader>
            <form className="flex min-h-0 flex-col gap-4" aria-busy={pending} aria-describedby={error ? errorId : undefined}
                onSubmit={async event => {
                    event.preventDefault();
                    if (saving.current) return;
                    const invalid = validate?.();
                    if (invalid) {setError(invalid); return;}
                    saving.current = true;
                    setPending(true); setError(undefined);
                    try {await onSave(); setOpen(false);}
                    catch (reason) {setError(`Speichern fehlgeschlagen. ${errorDescription(reason)}`);}
                    finally {saving.current = false; setPending(false);}
                }}>
                <fieldset disabled={pending} className="min-h-0 space-y-4 overflow-y-auto border-0 p-1">{children}</fieldset>
                {error && <p id={errorId} role="alert" className="shrink-0 text-sm text-destructive">{error}</p>}
                <DialogFooter className="shrink-0">
                    <Button type="button" variant="outline" disabled={pending} onClick={() => setOpen(false)}>Abbrechen</Button>
                    <Button type="submit" disabled={pending}>{pending ? "Speichert…" : "Speichern"}</Button>
                </DialogFooter>
            </form>
        </DialogContent>
    </Dialog>;
}
